// @flow
import React, { Component } from 'react';
import { Accordion, Icon, Grid, Divider, Header, Button, Message, Label, Segment } from 'semantic-grab-ui/src';
import './TreatmentForm.scss';
import formComponent from 'components/decorators/form_component';
import type { TridentCampaignDetails } from 'types/trident-campaigns';
import { getListOfHedwigActions } from 'utils/new-trident-utils';
import SubActionMetaForm from './SubActionMetaForm';
import _ from 'lodash';
import Toastr from 'utils/toastr-util';

interface Props {
  model: TridentCampaignDetails;
  canActivate: boolean;
  currentNode: string;
  parentNode: string;
  parentKeyList: string[];
  onModelChange: (model?: object) => void;
  handleInputChange: Function;
  handleControlBlur: Function;
  handleCloseModal: Function;
  isAdd: boolean;
  handleDeleteNode: Function;
  isValid: Function;
  error: Record<string, any>;
  listRuleTypes: object;
  eventType: string;
  enableDynamicPoint: boolean;
  freezeSubActions: boolean;
}

type ExternalValidationErrorMap = Record<number, Record<string, string>>;

interface State {
  activeIndex: number;
  originalModel: TridentCampaignDetails;
  error: Record<number, string[]>;
  externalValidationErrors: ExternalValidationErrorMap;
}

interface SubAction {
  type: string;
  meta: object;
}

interface FormInstance {
  getWrappedInstance?: () => FormInstance;
  getFormErrors?: () => string[];
}

export class SubActionForm extends Component<Props, State> {
  actionForm: Record<number, FormInstance | null>;
  hedwigActions: string[];
  subActionEnd: HTMLDivElement | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      activeIndex: 0,
      originalModel: this.props.model,
      error: {},
      externalValidationErrors: {},
    };
    this.actionForm = {};
    this.hedwigActions = getListOfHedwigActions(this.props.listRuleTypes);
  }

  scrollToBottom = () => {
    this.subActionEnd.scrollIntoView({ behavior: 'smooth' });
  }

  componentDidMount() {
    this.scrollToBottom();
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (_.get(nextProps.model.nodes[nextProps.currentNode], 'data', []).length !== _.get(this.props.model.nodes[this.props.currentNode], 'data', []).length) {
      const newActiveIndex = _.get(nextProps.model.nodes[nextProps.currentNode], 'data', []).length - 1;
      this.setState({ activeIndex: newActiveIndex });
    }
  }

  clearSubActionErrors = (index: number) => {
    const updatedError = { ...this.state.error };
    delete updatedError[index];
    this.setState({ error: updatedError });
  }

  handleExternalValidationChange = (
    index: number,
    validationKey: string,
    isValid: boolean,
    errorMessage?: string | null,
  ) => {
    this.setState((prevState) => {
      const nextErrors = { ...prevState.externalValidationErrors };
      const errorsForIndex = { ...(nextErrors[index] || {}) };

      if (!isValid && errorMessage && errorMessage.trim()) {
        errorsForIndex[validationKey] = errorMessage;
      } else {
        delete errorsForIndex[validationKey];
      }

      if (_.isEmpty(errorsForIndex)) {
        delete nextErrors[index];
      } else {
        nextErrors[index] = errorsForIndex;
      }

      return { externalValidationErrors: nextErrors };
    });
  }

  handleClick = (e: React.MouseEvent, titleProps: { index: number }) => {
    const { index } = titleProps;
    const { activeIndex } = this.state;
    const newIndex = activeIndex === index ? activeIndex : index;

    this.setState({ activeIndex: newIndex });
  }

  shiftIndexedMapAfterDelete(source: Record<number, any>, deletedIndex: number): Record<number, any> {
    const nextMap: Record<number, any> = {};
    Object.entries(source).forEach(([key, value]) => {
      const keyNum = Number(key);
      if (keyNum === deletedIndex) return;
      const shiftedIndex = keyNum > deletedIndex ? keyNum - 1 : keyNum;
      nextMap[shiftedIndex] = value;
    });
    return nextMap;
  }

  handleDeleteSubAction = (index: number) => {
    const updatedModel = _.cloneDeep(this.props.model);
    const actionList = _.get(updatedModel.nodes[this.props.currentNode], 'data', []);
    actionList.splice(index, 1);
    _.set(updatedModel.nodes[this.props.currentNode], 'data', actionList);

    this.actionForm = this.shiftIndexedMapAfterDelete(this.actionForm, index);
    this.setState({
      externalValidationErrors: this.shiftIndexedMapAfterDelete(this.state.externalValidationErrors, index),
      error: this.shiftIndexedMapAfterDelete(this.state.error, index),
    });
    this.props.onModelChange(updatedModel);
  }

  cancelActionModal = () => {
    if (this.props.isAdd) {
      // eslint-disable-next-line no-alert
      const confirmValue = confirm('Cancel will delete this inactive node. Are you sure to delete this?');
      if (!confirmValue) return;
      this.props.handleDeleteNode(this.props.parentNode, this.props.currentNode, -1);
    } else {
      this.props.onModelChange(this.state.originalModel);
    }
    this.props.handleCloseModal();
  }

  isSubActionFormValid = () => {
    let isValid = true;
    const errorSubActionMap: Record<number, string[]> = {};
    const keys = _.keysIn(this.actionForm);
    keys.forEach((key) => {
      const keyNum = parseInt(key, 10);
      let form = this.actionForm[keyNum];
      if (form !== null && _.isFunction(form.getWrappedInstance)) {
        form = form.getWrappedInstance();
      }
      if (form !== null && _.isFunction(form.getFormErrors)) {
        const errors = form.getFormErrors();
        if (errors.length > 0) {
          isValid = false;
        }
        errorSubActionMap[keyNum] = errors;
      }
    });
    Object.entries(this.state.externalValidationErrors).forEach(([index, validationByKey]) => {
      const keyNum = parseInt(index, 10);
      if (!errorSubActionMap[keyNum]) {
        errorSubActionMap[keyNum] = [];
      }
      Object.values(validationByKey).forEach((message) => {
        errorSubActionMap[keyNum].push(message);
      });
      isValid = false;
    });

    this.setState({ error: errorSubActionMap });
    return isValid;
  }

  saveAction = () => {
    if (!this.isSubActionFormValid() || !this.props.isValid()) {
      Toastr.error('There are errors in sub-action forms');
    } else {
      this.props.handleCloseModal();
    }
  }

  render() {
    const { model, currentNode, freezeSubActions } = this.props;
    const { activeIndex } = this.state;
    const style = {
      cursor: 'pointer',
    };
    const isActionListEmpty = _.isEmpty(_.get(model.nodes[currentNode], 'data', []));
    return (
      <div>
        <div className="model-button">
          <Grid>
            <Grid.Column textAlign="right">
              <Button.Group>
                <Button
                  onClick={this.cancelActionModal}
                >
                  Cancel
                </Button>
                <Button.Or />
                <Button
                  positive
                  onClick={this.saveAction}
                >
                  Save
                </Button>
              </Button.Group>
            </Grid.Column>
          </Grid>
        </div>
        {!_.isEmpty(_.get(this.props.error, 'actionNode', [])) &&
          <Message negative size="tiny">
            <Message.Header>
              <span className="validation-icon" />
              {this.props.error.actionNode[0]}
            </Message.Header>
          </Message>
        }
        {isActionListEmpty &&
          <Segment placeholder size="tiny" textAlign="center">
            <Header icon>
              <Icon name="tag" />
              No sub-actions are created yet!.
            </Header>
          </Segment>
        }
        {!isActionListEmpty &&
          <Accordion fluid styled>
            {_.get(model.nodes[currentNode], 'data', []).map((subAction: SubAction, index: number) => {
              return (
                <div key={index}>
                  <Accordion.Title active={activeIndex === index} index={index} onClick={this.handleClick} key={`${index}${'title-actionData'}`}>
                    <Icon name="dropdown" />
                    {`${subAction.type}`}
                    {!_.isEmpty(this.state.error[index]) &&
                      <div>
                        <span style={{ color: 'red' }}> sub-action contains errors </span>
                        <Icon name="hand point left outline" color="red" />
                        <div style={{ boxShadow: '0px 0px 0px 1px #e0b4b4 inset, 0px 0px 0px 0px rgb(0 0 0 / 0%)', padding: '12px 18px', width: 'fit-content', margin: '12px 0px', backgroundColor: '#FFF6F6', borderRadius: '5px' }}>
                          {
                            this.state.error[index].map((error: string, errIndex: number) => {
                              let errMsg = error.replace(/Nodes [0-9]*.data [0-9]*.meta/, '').trim();

                              // Generic Action meta always have 'generic params' prefix.
                              if (errMsg.startsWith('generic params')) {
                                errMsg = errMsg.replace('generic params', '').trim();
                              }

                              if (errMsg && errMsg.length > 2) {
                                errMsg = errMsg.charAt(0).toUpperCase() + errMsg.slice(1);
                              }
                              return (<div key={errIndex} style={{ color: '#912D2B' }}>
                                {errMsg}
                              </div>);
                            })}
                        </div>
                      </div>
                    }
                  </Accordion.Title>
                  <Accordion.Content active={activeIndex === index} key={`${index}content-actionData`}>
                    <SubActionMetaForm
                      key={`${index}-actionForm`}
                      onModelChange={this.props.onModelChange}
                      model={this.props.model}
                      currentNode={currentNode}
                      parentKeyList={this.props.parentKeyList}
                      subActionIndex={index}
                      actionType={subAction.type}
                      eventType={this.props.eventType}
                      enableDynamicPoint={this.props.enableDynamicPoint}
                      onSubActionValidationChange={(validationKey: string, isValid: boolean, errorMessage?: string | null) =>
                        this.handleExternalValidationChange(index, validationKey, isValid, errorMessage)
                      }
                      formRef={(form: FormInstance | null) => (this.actionForm[index] = form)}
                      clearErrors={() => this.clearSubActionErrors(index)}
                    />
                    <Divider />
                    {!freezeSubActions ?
                      <Grid columns="equal">
                        <Grid.Column width={8}>
                          <Label
                            size="small"
                            color="violet"
                            style={style}
                            onClick={() => this.handleDeleteSubAction(index)}
                          >
                            <Icon name="trash alternate" /> Delete
                          </Label>
                        </Grid.Column>
                      </Grid> : null}
                  </Accordion.Content>
                </div>
              );
            })}
          </Accordion>
        }
        <div
          style={{ float: 'left', clear: 'both' }}
          ref={(el) => { this.subActionEnd = el; }}
        />
      </div>
    );
  }
}

export default formComponent()(SubActionForm);
